import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import { OrganizationSettings } from '../settings/Organization';
import { DefaultsSettings } from '../settings/Defaults';
import { BillingSettings } from '../settings/Billing';
import { SocialSettings } from '../settings/Social';
import { EventAuditSettings } from '../settings/EventAudit';
import { TeamSettings } from '../settings/Team';
import { useTinycmsPlans } from '../account/tinycmsPlans';
import { IntegrationsSettings } from '../settings/Integrations';
import { ResearchSettings } from '../settings/Research';
import { TaxonomySettings } from '../settings/Taxonomy';
import { TopicCustomFieldsSettings } from '../settings/TopicCustomFields';
import { PublicSiteSettings } from '../settings/PublicSite';
import { CollectionDataSettings } from '../settings/CollectionData';
import { CaptureDevicesSettings } from '../settings/CaptureDevices';
import { EbayConnectionSettings } from '../settings/EbayConnection';
import { PublishingSettings } from '../settings/Publishing';
import { DomainsSettings } from '../settings/Domains';
import { AnalyticsSettings } from '../settings/Analytics';
import { Profile } from '../settings/Profile';
import { useAccount } from '../../context/AccountContext';
import { useAuth } from '../../context/AuthContext';
import { useProduct, ProductId } from '../../context/ProductContext';
import { GET_ACCOUNT_DETAILS } from '../../graphql';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

type SettingSection =
  | 'all'
  | 'profile'
  | 'organization'
  | 'defaults'
  | 'billing'
  | 'social'
  | 'audit'
  | 'team'
  | 'integrations'
  | 'research'
  | 'taxonomy'
  | 'topicFields'
  | 'publicSite'
  | 'devices'
  | 'ebay'
  | 'collectionData'
  | 'publishing'
  | 'domains'
  | 'analytics';

interface NavItem {
  id: SettingSection;
  label: string;
  meta?: string;
  products?: ProductId[];
}

interface IndexItem {
  name: string;
  desc: string;
  go: () => void;
}

interface IndexGroup {
  area: string;
  where: string;
  items: IndexItem[];
}

/** The design's account menu. Content and Site settings are rows in the index, not items here. */
const ACCOUNT_NAV: NavItem[] = [
  { id: 'all', label: 'All settings' },
  { id: 'organization', label: 'General' },
  { id: 'profile', label: 'Profile' },
  { id: 'team', label: 'Team' },
  { id: 'integrations', label: 'Connections' },
  { id: 'billing', label: 'Plan & billing' },
  { id: 'audit', label: 'Activity log' },
];

/**
 * Screens the review did not redraw. They stay reachable for the product that
 * owns them, after the design menu.
 */
const EXTRA_NAV: NavItem[] = [
  { id: 'defaults', label: 'Preferences', products: ['reach'] },
  { id: 'social', label: 'Connected accounts', products: ['reach'] },
  { id: 'taxonomy', label: 'Taxonomy', products: ['collect'] },
  { id: 'topicFields', label: 'Custom fields', products: ['collect'] },
  { id: 'publicSite', label: 'Public site', products: ['collect'] },
  { id: 'devices', label: 'Capture devices', products: ['collect'] },
  { id: 'ebay', label: 'eBay', products: ['collect'] },
  { id: 'collectionData', label: 'Your data', products: ['collect'] },
];

/** TinyCMS moved these out of Account. Old links land on the page that owns them now. */
const MOVED: Partial<Record<SettingSection, string>> = {
  taxonomy: '/content/settings#taxonomy',
  topicFields: '/content/settings#fields',
  publishing: '/content/settings#publishing',
  publicSite: '/site#visibility',
  domains: '/site#domain',
  analytics: '/site#analytics',
};

function AllSettings({ groups }: { groups: IndexGroup[] }) {
  return (
    <div>
      <h1 className="ink-h1">All settings</h1>
      <p className="ink-lede">
        Each setting lives next to the thing it controls. Here they are in one place.
      </p>
      {groups.map((group) => (
        <div className="ink-set-group" key={group.area}>
          <div className="ink-set-head">
            <div style={{ font: '600 17px/1.3 var(--ink-font)' }}>{group.area}</div>
            <span className="ink-set-where">{group.where}</span>
          </div>
          <div className="ink-set-list">
            {group.items.map((item) => (
              <button key={item.name} type="button" className="ink-set-row" onClick={item.go}>
                <span className="ink-set-name">{item.name}</span>
                <span className="ink-set-desc">{item.desc}</span>
                <span className="ink-set-go">→</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SettingsLayout() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requested = params.get('section') as SettingSection | null;
  const [activeSection, setActiveSection] = useState<SettingSection>(requested || 'all');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { logout } = useAuth();
  const onProfile = location.pathname.endsWith('/profile');
  const { product } = useProduct();
  const { selectedAccount } = useAccount();
  const isTiny = product === 'atomizer' || product === 'tinycms';

  const { data: accountDetails } = useQuery<{
    account?: { users?: unknown[]; activeOrder?: { plan?: string } };
  }>(GET_ACCOUNT_DETAILS, {
    variables: { id: selectedAccount?.id },
    skip: !selectedAccount?.id,
  });
  const seats = accountDetails?.account?.users?.length;
  // The team cap is the TinyCMS plan's; accounts without one show a plain count.
  const { plans: tinycmsPlans } = useTinycmsPlans();
  const seatCap = isTiny
    ? tinycmsPlans.find((p) => p.key === accountDetails?.account?.activeOrder?.plan)?.maxMembers
    : 10;

  const extraItems = EXTRA_NAV.filter((item) => !item.products || item.products.includes(product));
  const navItems = ACCOUNT_NAV.map((item) =>
    item.id === 'team' && seats != null
      ? { ...item, meta: seatCap ? `${seats} / ${seatCap}` : `${seats}` }
      : item
  ).concat(extraItems);

  const isVisible = (section: SettingSection) => navItems.some((item) => item.id === section);

  useEffect(() => {
    if (requested && isTiny && MOVED[requested]) {
      navigate(MOVED[requested] as string, { replace: true });
      return;
    }
    if (requested && isVisible(requested)) setActiveSection(requested);
    else if (!requested && !onProfile) setActiveSection('all');
    else if (!isVisible(activeSection) && activeSection !== 'all') setActiveSection('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectedAccount?.id, requested, onProfile]);

  const openSection = (id: SettingSection) => {
    setActiveSection(id);
    if (id === 'profile') {
      navigate('/settings/profile');
      return;
    }
    if (id === 'all') {
      navigate('/settings');
      return;
    }
    navigate(`/settings?section=${id}`);
  };

  const groups: IndexGroup[] = [];
  if (isTiny) {
    groups.push({
      area: 'Content settings',
      where: 'Content → Content settings',
      items: [
        {
          name: 'Taxonomy',
          desc: 'Allowed terms for classifying content',
          go: () => navigate('/content/settings#taxonomy'),
        },
        {
          name: 'Custom fields',
          desc: 'Extra structured data on each article',
          go: () => navigate('/content/settings#fields'),
        },
        {
          name: 'Publishing rules',
          desc: 'Copy edit, style guide, voice',
          go: () => navigate('/content/settings#publishing'),
        },
      ],
    });
    groups.push({
      area: 'Site',
      where: 'Site',
      items: [
        {
          name: 'Visibility',
          desc: 'Public, password, or offline',
          go: () => navigate('/site#visibility'),
        },
        {
          name: 'Surface & agent permissions',
          desc: 'Who an agent may change in each part of the site',
          go: () => navigate('/site#surface'),
        },
        {
          name: 'Theme',
          desc: 'Preset, colours, fonts, home layout',
          go: () => navigate('/site#theme'),
        },
        {
          name: 'Identity',
          desc: 'Title, tagline, logo, cover image',
          go: () => navigate('/site#identity'),
        },
        {
          name: 'Domain',
          desc: 'Free address and custom domains',
          go: () => navigate('/site#domain'),
        },
        {
          name: 'Analytics & code',
          desc: 'GTM, GA4, Amplitude, consent, custom code',
          go: () => navigate('/site#analytics'),
        },
      ],
    });
  }
  groups.push({
    area: 'Account',
    where: 'Avatar → Account',
    items: [
      {
        name: 'Profile',
        desc: 'Your name, sign-in, and timezone',
        go: () => openSection('profile'),
      },
      {
        name: 'General',
        desc: 'Organization name, contact, frequency',
        go: () => openSection('organization'),
      },
      { name: 'Team', desc: 'Members, roles, invites', go: () => openSection('team') },
      {
        name: 'Connections',
        desc: 'Agents (MCP), sources, WordPress, API keys',
        go: () => openSection('integrations'),
      },
      {
        name: 'Plan & billing',
        desc: 'Subscription, payment, invoices',
        go: () => openSection('billing'),
      },
      {
        name: 'Activity log',
        desc: 'Every change and approval, by people and agents',
        go: () => openSection('audit'),
      },
    ],
  });
  if (product === 'reach') {
    groups.push({
      area: 'PostMaster',
      where: 'Account',
      items: [
        {
          name: 'Preferences',
          desc: 'Link shortening, first comment, and campaign defaults',
          go: () => openSection('defaults'),
        },
        {
          name: 'Connected accounts',
          desc: 'Social profiles this account can publish to',
          go: () => openSection('social'),
        },
      ],
    });
  }
  if (product === 'collect') {
    groups.push({
      area: 'Collection',
      where: 'Account',
      items: [
        {
          name: 'Taxonomy',
          desc: 'Allowed terms for classifying a collection',
          go: () => openSection('taxonomy'),
        },
        {
          name: 'Custom fields',
          desc: 'Extra structured data on each item',
          go: () => openSection('topicFields'),
        },
        {
          name: 'Public site',
          desc: 'Address, title, and whether the collection is live',
          go: () => openSection('publicSite'),
        },
        {
          name: 'Capture devices',
          desc: 'Cameras that photograph the collection',
          go: () => openSection('devices'),
        },
        {
          name: 'eBay',
          desc: 'The eBay account this collection sells through',
          go: () => openSection('ebay'),
        },
        {
          name: 'Your data',
          desc: 'Export the collection',
          go: () => openSection('collectionData'),
        },
      ],
    });
  }

  const renderContent = () => {
    if (activeSection !== 'all' && !isVisible(activeSection))
      return <AllSettings groups={groups} />;

    switch (activeSection) {
      case 'all':
        return <AllSettings groups={groups} />;
      case 'profile':
        return <Profile />;
      case 'organization':
        return <OrganizationSettings />;
      case 'defaults':
        return <DefaultsSettings />;
      case 'billing':
        return <BillingSettings />;
      case 'social':
        return <SocialSettings />;
      case 'audit':
        return <EventAuditSettings />;
      case 'team':
        return <TeamSettings />;
      case 'integrations':
        return <IntegrationsSettings />;
      case 'research':
        return <ResearchSettings />;
      case 'taxonomy':
        return <TaxonomySettings />;
      case 'topicFields':
        return <TopicCustomFieldsSettings />;
      case 'publicSite':
        return <PublicSiteSettings />;
      case 'devices':
        return <CaptureDevicesSettings />;
      case 'ebay':
        return <EbayConnectionSettings />;
      case 'collectionData':
        return <CollectionDataSettings />;
      case 'publishing':
        return <PublishingSettings />;
      case 'domains':
        return <DomainsSettings />;
      case 'analytics':
        return <AnalyticsSettings />;
      default:
        return <AllSettings groups={groups} />;
    }
  };

  const current = onProfile ? 'profile' : activeSection;

  return (
    <div className="ink-page" style={{ overflow: 'auto' }}>
      <div className="ink-account">
        <div>
          <div
            style={{
              font: '600 22px/1.2 var(--ink-font)',
              letterSpacing: '-0.01em',
              padding: '4px 10px 18px',
            }}
          >
            Account
          </div>
          <nav>
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-active={current === item.id ? 'true' : 'false'}
                onClick={() => openSection(item.id)}
              >
                {item.label}
                {item.meta ? <span> {item.meta}</span> : null}
              </button>
            ))}
            <button
              type="button"
              className="ink-account-signout"
              onClick={() => setShowLogoutDialog(true)}
            >
              Sign out
            </button>
          </nav>
        </div>
        <div style={{ maxWidth: 880, minWidth: 0 }}>{onProfile ? <Outlet /> : renderContent()}</div>
      </div>
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="bg-core-dark">
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out? You will need to sign in again to access your
              account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="bg-button-primary text-button-primary"
              onClick={() => setShowLogoutDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowLogoutDialog(false);
                logout('You have been successfully logged out.');
              }}
              className="bg-button-secondary text-button-secondary"
            >
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
